import { create } from "zustand";
import type { ActiveCoupon, CartChange } from "@/api/models/api-types";
import type { MonetizationSource } from "@/lib/monetization-analytics";
import type { PaymentRequiredCode } from "@/api/middleware/payment-required-middleware";

// ===== Enhanced Modal Types =====
type ModalType =
  | "credits" // Credits modal
  | "ai-model-selector" // AI model seçici
  | "llm-selector" // LLM seçici
  | "settings" // Ayarlar
  | "profile" // Profil
  | "confirmation" // Onay dialogu
  | "cancelSongGeneration" // Şarkı üretimi iptal
  | "cancelImageGeneration" // Görsel üretimi iptal
  | "unsavedChanges" // Kaydedilmemiş değişiklikler
  | "deleteChat" // Sohbet silme
  | "deleteFiles" // Dosya silme
  | "logout" // Çıkış yapma
  | "paymentMethodNotImplemented" // Ödeme yöntemi uyarısı
  | "paymentRequired" // Kredi bitti / ödeme gerekli uyarısı
  | "file-picker" // File picker modal
  | "cartChanges" // Sepetteki otomatik ürün değişimleri
  | null;

interface ModalData {
  title?: string;
  message?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  count?: number; // For deleteFiles modal
  coupon?: ActiveCoupon | null; // For paymentRequired modal (token-ended coupon)
  monetizationSource?: MonetizationSource; // For paymentRequired modal (mixpanel source)
  paymentCode?: PaymentRequiredCode; // For paymentRequired modal (402'nin sebebi)
  paymentRequestId?: string; // Async coupon sonucu yalnız kendi 402 modalını güncelleyebilir
  [key: string]: any; // Generic data için
}

// ===== Enhanced Modal Store Interface =====
interface EnhancedModalStore {
  // State
  isOpen: boolean;
  type: ModalType;
  data?: ModalData;

  // Actions
  onOpen: (type: ModalType, data?: ModalData) => void;
  onClose: () => void;

  // Specific Modal Actions (convenience methods)
  openCreditsModal: () => void;
  openConfirmation: (
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ) => void;
  openAIModelSelector: (data?: any) => void;
  openLLMSelector: (data?: any) => void;
  openSettings: () => void;
  openProfile: () => void;
  openUnsavedChanges: (onConfirm: () => void, onCancel?: () => void) => void;
  openDeleteChat: (onConfirm: () => void, onCancel?: () => void) => void;
  openDeleteFiles: (
    count: number,
    onConfirm: () => void,
    onCancel?: () => void,
  ) => void;
  openLogout: (onConfirm: () => void, onCancel?: () => void) => void;
  openPaymentMethodNotImplemented: () => void;
  openPaymentRequiredModal: (data?: ModalData) => void;
  openFilePicker: (data?: any) => void;
  openCartChanges: (changes: CartChange[]) => void;

  // Getters
  isCreditsModalOpen: () => boolean;
  isConfirmationOpen: () => boolean;
  isAIModelSelectorOpen: () => boolean;
  isLLMSelectorOpen: () => boolean;
  isSettingsOpen: () => boolean;
  isFilePickerOpen: () => boolean;
  isProfileOpen: () => boolean;
}

// ===== Enhanced Modal Store Implementation =====
const useModalStore = create<EnhancedModalStore>((set, get) => ({
  // ===== State =====
  isOpen: false,
  type: null,
  data: undefined,

  // ===== Core Actions =====
  onOpen: (type: ModalType, data?: ModalData) => {
    set({ isOpen: true, type, data });
  },

  onClose: () => {
    set({ isOpen: false, type: null, data: undefined });
  },

  // ===== Specific Modal Actions =====
  openCreditsModal: () => {
    set({ isOpen: true, type: "credits", data: undefined });
  },

  openConfirmation: (
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ) => {
    set({
      isOpen: true,
      type: "confirmation",
      data: {
        message,
        onConfirm,
        onCancel: onCancel || (() => get().onClose()),
      },
    });
  },

  openAIModelSelector: (data?: any) => {
    set({ isOpen: true, type: "ai-model-selector", data });
  },

  openLLMSelector: (data?: any) => {
    set({ isOpen: true, type: "llm-selector", data });
  },

  openSettings: () => {
    set({ isOpen: true, type: "settings", data: undefined });
  },

  openProfile: () => {
    set({ isOpen: true, type: "profile", data: undefined });
  },

  openUnsavedChanges: (onConfirm: () => void, onCancel?: () => void) => {
    set({
      isOpen: true,
      type: "unsavedChanges",
      data: {
        onConfirm,
        onCancel: onCancel || (() => get().onClose()),
      },
    });
  },

  openDeleteChat: (onConfirm: () => void, onCancel?: () => void) => {
    set({
      isOpen: true,
      type: "deleteChat",
      data: {
        onConfirm,
        onCancel: onCancel || (() => get().onClose()),
      },
    });
  },

  openLogout: (onConfirm: () => void, onCancel?: () => void) => {
    set({
      isOpen: true,
      type: "logout",
      data: {
        onConfirm,
        onCancel: onCancel || (() => get().onClose()),
      },
    });
  },

  openDeleteFiles: (
    count: number,
    onConfirm: () => void,
    onCancel?: () => void,
  ) => {
    set({
      isOpen: true,
      type: "deleteFiles",
      data: {
        count,
        onConfirm,
        onCancel: onCancel || (() => get().onClose()),
      },
    });
  },

  openPaymentMethodNotImplemented: () => {
    set({
      isOpen: true,
      type: "paymentMethodNotImplemented",
      data: undefined,
    });
  },

  openPaymentRequiredModal: (data?: ModalData) => {
    set({
      isOpen: true,
      type: "paymentRequired",
      data,
    });
  },

  openFilePicker: (data?: any) => {
    set({
      isOpen: true,
      type: "file-picker",
      data,
    });
  },

  openCartChanges: (changes) => {
    set({
      isOpen: true,
      type: "cartChanges",
      data: { changes },
    });
  },

  // ===== Getters =====
  isCreditsModalOpen: () => {
    const state = get();
    return state.isOpen && state.type === "credits";
  },

  isConfirmationOpen: () => {
    const state = get();
    return state.isOpen && state.type === "confirmation";
  },

  isAIModelSelectorOpen: () => {
    const state = get();
    return state.isOpen && state.type === "ai-model-selector";
  },

  isLLMSelectorOpen: () => {
    const state = get();
    return state.isOpen && state.type === "llm-selector";
  },

  isSettingsOpen: () => {
    const state = get();
    return state.isOpen && state.type === "settings";
  },

  isFilePickerOpen: () => {
    const state = get();
    return state.isOpen && state.type === "file-picker";
  },

  isProfileOpen: () => {
    const state = get();
    return state.isOpen && state.type === "profile";
  },
}));

export default useModalStore;
export { useModalStore };

// ===== Export Types =====
export type { ModalType, ModalData, EnhancedModalStore };
